import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  EmailVerificationIcon,
  FlexControlledForm,
  useDebounceState,
  useResponsive,
} from '@common-fe/common-fe';

import REGEXP from '@/common/regexp';
import ResendCodeButton from '@/modules/user/components/Verification/components/ResendCodeButton';
import VerificationCodeModal from '@/modules/user/components/Verification/components/VerificationCodeModal';
import useVerificationCodeFields, {
  FormValues,
} from '@/modules/user/components/Verification/hooks/useVerificationCodeFields';
import useSendVerificationCodeQuery from '@/modules/user/components/Verification/queries/useSendVerificationCode.query';
import useValidateVerifyCodeQuery from '@/modules/user/components/Verification/queries/useValidateVerifyCode.query';
import {
  VerificationErrorText,
  VerificationType,
} from '@/modules/user/components/Verification/verification.types';
import { useValidateForm } from '@/utils/hooks';

interface Props {
  email: string;
  onSetVisible: (value: boolean) => void;
  onBack?: () => void;
  onNext: () => void;
}

const EmailVerificationCodeModal: React.FC<Props> = ({ email, onSetVisible, onBack, onNext }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const [verificationErrorText, setVerificationErrorText] = useState('');
  const fields = useVerificationCodeFields({ verificationErrorText });
  const [state, handleSetState] = useState<FormValues>();
  const { values, setValues } = useDebounceState<FormValues>();
  const { handlePreSubmit, hasErrors } = useValidateForm(fields);
  const { validateVerifyCode, isLoading } = useValidateVerifyCodeQuery();
  const { sendVerificationCode } = useSendVerificationCodeQuery();

  const handleConfirm = useCallback(async () => {
    try {
      const isSuccess = handlePreSubmit(state);
      if (isSuccess) {
        setVerificationErrorText('');
        const response = await validateVerifyCode({
          code: state?.verificationCode?.replace(REGEXP.SPACE_SYMBOL, '') || '',
          value: email,
          type: VerificationType.EMAIL,
          emailSource: 'EMPLOYEE',
        });
        if (!response?.verified) {
          throw new Error();
        }
        onNext();
      }
    } catch (e) {
      setVerificationErrorText(VerificationErrorText.CODE_IS_INVALID);
    }
  }, [state, email, onNext, validateVerifyCode, handlePreSubmit]);

  const handleResendCode = useCallback(async () => {
    await sendVerificationCode({
      type: VerificationType.EMAIL,
      value: email,
    });
  }, [sendVerificationCode, email]);

  useEffect(() => {
    if (values) {
      handleSetState(values);
      setVerificationErrorText('');
    }
  }, [handleSetState, values]);

  return (
    <VerificationCodeModal
      testId="email-verification-code"
      icon={<EmailVerificationIcon size="128px" />}
      title={t('Email Verification')}
      value={email}
      isLoading={isLoading}
      onSetVisible={onSetVisible}
      onBack={onBack}
      onConfirm={handleConfirm}
      form={
        <FlexControlledForm
          fields={fields}
          onChangeValues={setValues}
          formStyle={{ margin: '24px' }}
          showError={hasErrors || !!verificationErrorText}
          editMode
        >
          <Box width="320px" alignSelf={isMobile ? 'start' : 'end'}>
            <ResendCodeButton onResend={handleResendCode} />
          </Box>
        </FlexControlledForm>
      }
    />
  );
};

export default EmailVerificationCodeModal;
